/* istanbul ignore file */

import { useCallback } from 'react';
import { useNavigation } from '@react-navigation/native';

import { useCart } from '../../../../hooks';
import type { LocationsScreenProps } from '../../../../navigation/AppNavigation.props';
import { useConflictReview } from '../../../../screens/ReorderingScreen';
import type { LocationSearchMachineContext } from '../../machine';
import { useNavigateToMenuFromLocation } from '../useNavigateToMenuFromLocation';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Provides navigation actions and helpers for Location Search.
 */
export const useLocationSearchNavigation = () => {
  const { cart, isCartEmpty } = useCart();
  const conflictReview = useConflictReview();
  const navigateToMenu = useNavigateToMenuFromLocation();
  const { setParams } = useNavigation<LocationsScreenProps['navigation']>();

  // ─── Helpers ─────────────────────────────────────────────────────────

  /**
   * Initiates a navigation event for pickup and outpost locations
   * based on the active location context.
   */
  const navigateToLocation = useCallback(
    (context: LocationSearchMachineContext) => {
      const { locationSearchType: orderType, activeLocation } = context;
      const cartRestaurantSlug = cart?.restaurant?.slug;

      if (!activeLocation) return;

      const { restaurantName, restaurantId, restaurantSlug, isOutpost } =
        activeLocation;

      // ─── Possible Reorder ────────────────────────────────────────────────

      const shouldStartReorder =
        !isCartEmpty && cartRestaurantSlug !== restaurantSlug;

      // if your bag belongs to a different restaurant, start reorder flow
      if (shouldStartReorder) {
        void conflictReview({
          orderId: cart?.id,
          orderType: isOutpost ? 'outpost' : 'pickup',
          restaurantId,
          restaurantName,
          restaurantSlug,
        });

        return;
      }

      // ─────────────────────────────────────────────────────────────────────

      navigateToMenu({
        orderType,
        restaurantId,
        restaurantName,
        restaurantSlug,
      });
    },
    [
      cart?.id,
      cart?.restaurant?.slug,
      conflictReview,
      isCartEmpty,
      navigateToMenu,
    ],
  );

  /**
   * Initiates a navigation event for delivery location
   * based on the active location context.
   */
  const navigateToDeliveryLocation = useCallback(
    (context: LocationSearchMachineContext) => {
      const { deliveryLocation } = context;

      if (!deliveryLocation) return;

      const {
        addressId,
        restaurantId,
        restaurantName,
        restaurantSlug,
        restaurantDeliveryFee = 0,
        name: deliveryAddressName,
        deliveryVendorName,
        deliveryVendorRestaurantId,
      } = deliveryLocation;

      navigateToMenu({
        orderType: 'delivery',
        restaurantId,
        restaurantName,
        restaurantSlug,
        deliveryAddressName,
        deliveryOrderDetail: {
          addressId,
          tip: 0,
          deliveryFee: restaurantDeliveryFee,
          vendor: deliveryVendorName,
          vendorRestaurantId: deliveryVendorRestaurantId,
        },
      });
    },
    [navigateToMenu],
  );

  const setOrderChannelParams = useCallback(
    (orderChannel: string | undefined) => {
      setParams({ orderChannel });
    },
    [setParams],
  );

  // ─────────────────────────────────────────────────────────────────────

  return {
    navigateToLocation,
    navigateToDeliveryLocation,
    setOrderChannelParams,
  };
};
